import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { RuleSetStatus } from '@kkp-fraud/shared';
import { ApiError, COOKIE_MODE, ruleSetForbiddenMessage, ruleSetsApi } from '../api';
import { ActionMenu, type ActionMenuItem } from '../components/ActionMenu';
import { useShell } from '../components/AppLayout';
import { Dialog } from '../components/Dialog';
import { Field, useFieldErrors } from '../components/Field';
import { PageHead } from '../components/PageHead';
import { When } from '../components/When';
import { DEMO } from '../demo';
import { actorLabel } from '../format';
import { isDemoRuleSetId } from '../demo/ruleSets';
import { publishRuleSets, useRuleSets } from '../hooks/useRuleSets';
import { t } from '../i18n';
import { engineStatusOf, orderedSets, ruleSetFormProblems, setLabel, type RuleSetFormProblem, type RuleSetList, type RuleSetView } from '../ruleSets';
// R8b per-rule-set permissions
import { canManageRuleSets, mayOnSet } from '../session';
// Wave 5 rule set workspace
import { setRulesPath } from '../ruleSetRoutes';

/**
 * 2c rule sets — the Rule sets screen: every set, which ones evaluate transactions (the switch),
 * which one is the primary (it supplies the fail-safe policy), its live version, its rules and
 * its last release — and what the ENGINE says it loaded (`/readyz` `ruleSets`), because a set
 * switched on here is picked up by the engine's poll, not at once.
 *
 * Create / edit name and description / switch on or off / archive / restore / make primary.
 * There is no delete: a set's releases are the audit record, so archiving takes it out of
 * evaluation and off the pickers and keeps its history. Every write carries the registry's
 * etag; somebody else's change meanwhile is a 409 that reloads the list and says so.
 *
 * R8b: the list is the sets this account may see, each with its level. Switching a set on or
 * off and editing its name are for whoever may publish that set; creating a set, making one the
 * primary, archiving and restoring are Admin only. Where this service cannot write the registry
 * (Rules:Store=proxy, password mode) the list is the one set, read only.
 */
export function RuleSetsScreen() {
  const { settings, session, showToast, state } = useShell();
  const { list: registry, error, reload } = useRuleSets(settings);
  const [engine, setEngine] = useState<RuleSetStatus[] | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<RuleSetView | null>(null);
  const [archiving, setArchiving] = useState<RuleSetView | null>(null);
  // Wave 5
  const [duplicating, setDuplicating] = useState<RuleSetView | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!(COOKIE_MODE || DEMO)) return;
    let cancelled = false;
    ruleSetsApi.engineStatus(settings).then(
      (s) => !cancelled && setEngine(s),
      () => !cancelled && setEngine(null),
    );
    return () => {
      cancelled = true;
    };
  }, [settings]);

  // Password mode: no registry to ask — the one set is the document the shell has loaded.
  const list: RuleSetList | null =
    registry ??
    (state.loaded && !(COOKIE_MODE || DEMO)
      ? {
          source: 'proxy',
          canManage: false,
          etag: '',
          sets: [
            {
              id: state.loaded.ruleSetId,
              name: state.loaded.ruleSetId,
              enabled: true,
              primary: true,
              archived: false,
              version: state.loaded.version,
              ruleCount: state.loaded.ruleBased.length + state.loaded.patternBased.length,
              enabledRuleCount: [...state.loaded.ruleBased, ...state.loaded.patternBased].filter((r) => r.enabled).length,
              setRuleCount: (state.loaded.setRules ?? []).length,
            },
          ],
        }
      : null);

  // R8b: the registry's own writes are the Admin's; a set's switch and name are its publisher's.
  const registryWritable = !!list?.canManage && COOKIE_MODE;
  const mayManage = registryWritable && canManageRuleSets(session);
  const writable = (s: RuleSetView) => registryWritable && !isDemoRuleSetId(s.id) && mayOnSet(session, s.access, 'publish');
  const manageable = (s: RuleSetView) => mayManage && !isDemoRuleSetId(s.id);
  const sets = list ? orderedSets(list.sets) : [];
  const shown = sets.filter((s) => showArchived || !s.archived);
  const archivedCount = sets.filter((s) => s.archived).length;

  /** One registry write: the answer is the new list; a conflict reloads it and says why. */
  const write = async (call: (etag: string) => Promise<RuleSetList>, ok: string) => {
    if (!list) return false;
    setBusy(true);
    try {
      publishRuleSets(await call(list.etag));
      showToast(ok);
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'registry_conflict') void reload();
      showToast(errorText(e), 'error');
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead title={t('rulesets.title')} lead={t('rulesets.lead')} />

      {error && !registry && (
        <div className="banner error" role="status" data-testid="rulesets-error" style={{ marginBottom: 12 }}>
          {t('rulesets.error.load', { detail: error })}
        </div>
      )}
      {list && !list.canManage && (
        <div className="banner warn" role="status" data-testid="rulesets-readonly" style={{ marginBottom: 12 }}>
          {t('rulesets.readonly')}
        </div>
      )}

      <section className="card">
        <header className="card-head">
          <h2>{t('rulesets.list.title')}</h2>
          {list && (
            <span className="chip mute" data-testid="rulesets-count">
              {t('rulesets.list.count', { n: sets.length - archivedCount })}
            </span>
          )}
          <span className="spacer" />
          {archivedCount > 0 && (
            <label className="check small">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              {t('rulesets.archived.show', { n: archivedCount })}
            </label>
          )}
          {mayManage && (
            <button type="button" className="btn small" data-testid="rulesets-new" onClick={() => setCreating(true)}>
              {t('rulesets.new.action')}
            </button>
          )}
        </header>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('rulesets.col.set')}</th>
                <th style={{ width: 120 }}>{t('rulesets.col.enabled')}</th>
                <th style={{ width: 110 }}>{t('rulesets.col.version')}</th>
                <th style={{ width: 110 }} className="right">
                  {t('rulesets.col.rules')}
                </th>
                <th style={{ width: 200 }}>{t('rulesets.col.lastChange')}</th>
                <th style={{ width: 170 }}>{t('rulesets.col.engine')}</th>
                <th style={{ width: 44 }} aria-label={t('rulesets.col.actions')} />
              </tr>
            </thead>
            <tbody>
              {list === null && (
                <tr>
                  <td colSpan={7} className="empty">
                    {t('rulesets.loading')}
                  </td>
                </tr>
              )}
              {shown.map((s) => {
                const status = engineStatusOf(s.id, engine ?? undefined);
                const canWrite = writable(s);
                const canManage = manageable(s);
                const open = rulesPath(s);
                return (
                  <tr
                    key={s.id}
                    data-testid={`ruleset-row-${s.id}`}
                    // UX review P1-5: a row opens the set's rules; its name is the keyboard way in.
                    // Wave 5: an archived set opens too — read-only, its history visible.
                    className={s.archived ? 'muted clickable' : 'clickable'}
                    onClick={() => navigate(open)}
                  >
                    <td>
                      <div>
                        <Link className="rowlink" to={open} title={t('rulesets.open.link')} data-testid={`ruleset-open-${s.id}`} onClick={(e) => e.stopPropagation()}>
                          <b>{s.name}</b>
                        </Link>{' '}
                        {/* One line: an id never breaks mid-token; a long one is cut, whole in the tooltip. */}
                        <span
                          className="mono small muted nowrap"
                          title={s.id}
                          data-testid={`ruleset-id-${s.id}`}
                          style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}
                        >
                          {s.id}
                        </span>
                        {s.primary && (
                          <span className="chip accent" style={{ marginLeft: 6 }} data-testid={`ruleset-primary-${s.id}`}>
                            {t('rulesets.primary.chip')}
                          </span>
                        )}
                        {s.archived && (
                          <span className="chip mute" style={{ marginLeft: 6 }}>
                            {t('rulesets.archived.chip')}
                          </span>
                        )}
                        {isDemoRuleSetId(s.id) && (
                          <span className="chip warn" style={{ marginLeft: 6 }}>
                            {t('master.demo.chip')}
                          </span>
                        )}
                        {/* R8b: less than publish is worth saying; publish is the unmarked case. */}
                        {(s.access === 'view' || s.access === 'edit') && !isDemoRuleSetId(s.id) && (
                          <span className="chip mute" style={{ marginLeft: 6 }} data-testid={`ruleset-access-${s.id}`}>
                            {t(`perms.chip.${s.access}`)}
                          </span>
                        )}
                      </div>
                      {s.description && <div className="small muted">{s.description}</div>}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <label className="check small" title={s.primary ? t('rulesets.enabled.primary.tip') : undefined}>
                        <input
                          className="toggle"
                          type="checkbox"
                          role="switch"
                          aria-label={t('rulesets.enabled.a11y', { set: setLabel(s) })}
                          data-testid={`ruleset-enabled-${s.id}`}
                          // The primary is always on (it supplies the fail-safe); an archived set is never on.
                          disabled={!canWrite || busy || s.primary || s.archived}
                          checked={s.enabled && !s.archived}
                          onChange={(e) =>
                            void write(
                              (etag) => ruleSetsApi.update(settings, s.id, { enabled: e.target.checked }, etag),
                              t(e.target.checked ? 'rulesets.enabled.on.toast' : 'rulesets.enabled.off.toast', { set: setLabel(s) }),
                            )
                          }
                        />
                        {t(s.enabled && !s.archived ? 'rulesets.enabled.on' : 'rulesets.enabled.off')}
                      </label>
                    </td>
                    <td className="mono">{s.version ?? t('rulesets.version.none')}</td>
                    <td className="right mono">
                      {t('rulesets.rules.count', { enabled: s.enabledRuleCount, total: s.ruleCount })}
                      {s.setRuleCount > 0 && <div className="small muted">{t('rulesets.rules.setRules', { n: s.setRuleCount })}</div>}
                    </td>
                    <td className="small">
                      {s.lastRelease ? (
                        <>
                          <div>
                            <When iso={s.lastRelease.savedUtc} />
                          </div>
                          {/* The person, on one line — the `(u-…)` id is for the audit, kept in the tooltip (QA-R-19). */}
                          <div className="muted one-line" title={s.lastRelease.actor}>
                            {actorLabel(s.lastRelease.actor)}
                          </div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="small" data-testid={`ruleset-engine-${s.id}`}>
                      <EngineCell set={s} status={status} known={engine !== null} />
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {/* Edit first; the step that takes a set out of use last, set apart and confirmed (UX review P1-5, P2-10). */}
                      <RowMenu
                        label={t('rulesets.menu.a11y', { set: setLabel(s) })}
                        items={[
                          ...(canWrite ? [{ label: t('rulesets.menu.edit'), onSelect: () => setEditing(s) }] : []),
                          // Wave 5: who may create a set may duplicate one — its live release becomes the copy's draft.
                          ...(canManage && s.version !== null ? [{ label: t('rulesets.menu.duplicate'), onSelect: () => setDuplicating(s) }] : []),
                          ...(canManage && !s.primary && !s.archived
                            ? [
                                {
                                  label: t('rulesets.menu.primary'),
                                  disabled: !s.enabled || s.version === null,
                                  hint: t('rulesets.menu.primary.off.tip'),
                                  onSelect: () =>
                                    void write((etag) => ruleSetsApi.makePrimary(settings, s.id, etag), t('rulesets.primary.toast', { set: setLabel(s) })),
                                },
                                {
                                  label: t('rulesets.menu.archive'),
                                  disabled: s.enabled,
                                  hint: t('rulesets.menu.archive.off.tip'),
                                  separated: true,
                                  onSelect: () => setArchiving(s),
                                },
                              ]
                            : []),
                          ...(canManage && s.archived
                            ? [
                                {
                                  label: t('rulesets.menu.restore'),
                                  onSelect: () =>
                                    void write((etag) => ruleSetsApi.restore(settings, s.id, etag), t('rulesets.restored.toast', { set: setLabel(s) })),
                                },
                              ]
                            : []),
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="small muted" style={{ padding: '8px 12px' }}>
          {t('rulesets.footer.note')}
        </div>
      </section>

      {creating && list && (
        <RuleSetDialog
          existingIds={sets.map((s) => s.id)}
          busy={busy}
          onClose={() => setCreating(false)}
          onSave={async (form) => {
            const ok = await write(
              () => ruleSetsApi.create(settings, { id: form.id, name: form.name.trim(), ...(form.description.trim() ? { description: form.description.trim() } : {}), enabled: form.enabled }),
              t('rulesets.created.toast', { set: form.id }),
            );
            if (ok) setCreating(false);
          }}
        />
      )}
      {editing && list && (
        <RuleSetDialog
          initial={editing}
          existingIds={sets.map((s) => s.id)}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={async (form) => {
            const ok = await write(
              (etag) => ruleSetsApi.update(settings, editing.id, { name: form.name.trim(), description: form.description.trim() }, etag),
              t('rulesets.saved.toast', { set: editing.id }),
            );
            if (ok) setEditing(null);
          }}
        />
      )}
      {/* Wave 5: a copy of a set, switched off, whose first content is a draft awaiting publish. */}
      {duplicating && list && (
        <DuplicateDialog
          source={duplicating}
          existingIds={sets.map((s) => s.id)}
          busy={busy}
          onClose={() => setDuplicating(null)}
          onSave={async (form) => {
            const ok = await write(
              () =>
                ruleSetsApi.duplicate(settings, duplicating.id, {
                  id: form.id,
                  name: form.name.trim(),
                  ...(form.description.trim() ? { description: form.description.trim() } : {}),
                }),
              t('rulesets.duplicated.toast', { set: form.id, source: setLabel(duplicating) }),
            );
            if (ok) {
              setDuplicating(null);
              // Its page, where the copied draft waits to be published.
              navigate(setRulesPath(form.id, 'rb'));
            }
          }}
        />
      )}
      {archiving && list && (
        <Dialog
          title={t('rulesets.archive.title', { set: setLabel(archiving) })}
          testid="ruleset-archive-dialog"
          busy={busy}
          onClose={() => setArchiving(null)}
          foot={
            <>
              <span className="spacer" />
              <button type="button" className="btn" disabled={busy} onClick={() => setArchiving(null)}>
                {t('btn.cancel.label')}
              </button>
              <button
                type="button"
                className="btn danger"
                data-testid="ruleset-archive-go"
                disabled={busy}
                onClick={async () => {
                  const ok = await write(
                    (etag) => ruleSetsApi.archive(settings, archiving.id, etag),
                    t('rulesets.archived.toast', { set: setLabel(archiving) }),
                  );
                  if (ok) setArchiving(null);
                }}
              >
                {t('rulesets.menu.archive')}
              </button>
            </>
          }
        >
          <div className="small">{t('rulesets.archive.lead', { set: setLabel(archiving) })}</div>
        </Dialog>
      )}
    </>
  );
}

/** The row's `•••` — left out when the account may do nothing on the set (UX review P2-10). */
function RowMenu({ label, items }: { label: string; items: ActionMenuItem[] }) {
  if (items.length === 0) return null;
  return <ActionMenu variant="row" label={label} items={items} />;
}

/** Where a row opens: the set's page, on its Transaction tab (Wave 5) — an archived one read-only. */
function rulesPath(s: RuleSetView): string {
  return setRulesPath(s.id, 'rb');
}

/** What the engine says about the set: loaded (and which version), refused (why), or not asked. */
function EngineCell({ set, status, known }: { set: RuleSetView; status: RuleSetStatus | undefined; known: boolean }) {
  if (!known) return <span className="muted">—</span>;
  if (status?.loaded) {
    // The engine polls: a release made here shows the old version for a few seconds.
    const behind = set.version !== null && status.version !== set.version;
    return (
      <span className={behind ? 'chip warn' : 'chip ok'}>
        {behind ? t('rulesets.engine.behind', { version: status.version ?? '—' }) : t('rulesets.engine.loaded', { version: status.version ?? '—' })}
      </span>
    );
  }
  if (status) {
    return (
      <span className="chip bad" title={status.error}>
        {t('rulesets.engine.refused')}
      </span>
    );
  }
  return <span className="muted">{set.enabled && !set.archived ? t('rulesets.engine.pending') : t('rulesets.engine.off')}</span>;
}

interface RuleSetForm {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}

const PROBLEM_KEY: Record<RuleSetFormProblem, string> = {
  id: 'rulesets.problem.id',
  id_taken: 'rulesets.problem.id_taken',
  name: 'rulesets.problem.name',
  name_long: 'rulesets.problem.name_long',
  description_long: 'rulesets.problem.description_long',
};

/** New set (no `initial`) or edit one's name and description. The id is fixed once the set exists. */
function RuleSetDialog({
  initial,
  existingIds,
  busy,
  onClose,
  onSave,
}: {
  initial?: RuleSetView;
  existingIds: readonly string[];
  busy: boolean;
  onClose: () => void;
  onSave: (form: RuleSetForm) => Promise<void>;
}) {
  const creating = !initial;
  const [form, setForm] = useState<RuleSetForm>({
    id: initial?.id ?? '',
    name: initial?.name ?? '',
    description: initial?.description ?? '',
    enabled: false,
  });
  // A field's problem shows once the field was left, or after a click on Create / Save (UX review P2-5).
  const fields = useFieldErrors();
  const problems = ruleSetFormProblems(form, existingIds, creating);
  const errorOf = (...of: RuleSetFormProblem[]) => {
    const p = problems.find((x) => of.includes(x));
    return p ? t(PROBLEM_KEY[p]) : undefined;
  };

  return (
    <Dialog
      title={t(creating ? 'rulesets.new.title' : 'rulesets.edit.title', { set: initial?.id ?? '' })}
      testid="ruleset-dialog-frame"
      busy={busy}
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" disabled={busy} onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button
            type="button"
            className="btn primary"
            data-testid="ruleset-save"
            disabled={busy}
            onClick={() => {
              if (fields.attempt(problems.length === 0, document.querySelector('[data-testid="ruleset-dialog-frame"]'))) void onSave(form);
            }}
          >
            {t(creating ? 'rulesets.new.save' : 'rulesets.edit.save')}
          </button>
        </>
      }
    >
      <div className="stack" data-testid="ruleset-dialog">
        <Field
          label={t('rulesets.form.id.label')}
          required={creating}
          hint={t('rulesets.form.id.hint')}
          error={fields.show('id', errorOf('id', 'id_taken'))}
          name="id"
          form={fields}
          testid="ruleset-field-id"
        >
          <input type="text" className="mono" disabled={!creating} value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value.trim().toLowerCase() })} />
        </Field>
        <Field
          label={t('rulesets.form.name.label')}
          required
          error={fields.show('name', errorOf('name', 'name_long'))}
          name="name"
          form={fields}
          testid="ruleset-field-name"
        >
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field
          label={t('rulesets.form.description.label')}
          error={fields.show('description', errorOf('description_long'))}
          name="description"
          form={fields}
          testid="ruleset-field-description"
        >
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        {creating && (
          <label className="check small">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            {t('rulesets.form.enabled.label')}
          </label>
        )}
        {creating && <div className="small muted">{t('rulesets.form.create.note')}</div>}
      </div>
    </Dialog>
  );
}

// ---- Wave 5: duplicate a set --------------------------------------------------------

/**
 * Duplicate `source`: the new set's id (checked as a create's), its name ("<name> (copy)" to
 * start with) and description. The copy's content is the source's LIVE release, shown here; it
 * arrives as a draft on the new set's page, switched off, and goes live by the normal release.
 */
export function DuplicateDialog({
  source,
  existingIds,
  busy,
  onClose,
  onSave,
}: {
  source: RuleSetView;
  existingIds: readonly string[];
  busy: boolean;
  onClose: () => void;
  onSave: (form: { id: string; name: string; description: string }) => Promise<void>;
}) {
  const [form, setForm] = useState({ id: '', name: t('rulesets.duplicate.name', { name: source.name }), description: source.description ?? '' });
  const fields = useFieldErrors();
  const problems = ruleSetFormProblems(form, existingIds, true);
  const errorOf = (...of: RuleSetFormProblem[]) => {
    const p = problems.find((x) => of.includes(x));
    return p ? t(PROBLEM_KEY[p]) : undefined;
  };

  return (
    <Dialog
      title={t('rulesets.duplicate.title', { set: setLabel(source) })}
      testid="ruleset-duplicate-frame"
      busy={busy}
      onClose={onClose}
      foot={
        <>
          <span className="spacer" />
          <button type="button" className="btn" disabled={busy} onClick={onClose}>
            {t('btn.cancel.label')}
          </button>
          <button
            type="button"
            className="btn primary"
            data-testid="ruleset-duplicate-save"
            disabled={busy}
            onClick={() => {
              if (fields.attempt(problems.length === 0, document.querySelector('[data-testid="ruleset-duplicate-frame"]'))) void onSave(form);
            }}
          >
            {t('rulesets.duplicate.save')}
          </button>
        </>
      }
    >
      <div className="stack" data-testid="ruleset-duplicate">
        <div className="small" data-testid="ruleset-duplicate-source">
          {t('rulesets.duplicate.source', { set: setLabel(source), version: source.version ?? '—' })}
        </div>
        <Field
          label={t('rulesets.form.id.label')}
          required
          hint={t('rulesets.form.id.hint')}
          error={fields.show('id', errorOf('id', 'id_taken'))}
          name="id"
          form={fields}
          testid="ruleset-duplicate-id"
        >
          <input type="text" className="mono" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value.trim().toLowerCase() })} />
        </Field>
        <Field
          label={t('rulesets.form.name.label')}
          required
          error={fields.show('name', errorOf('name', 'name_long'))}
          name="name"
          form={fields}
          testid="ruleset-duplicate-name"
        >
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field
          label={t('rulesets.form.description.label')}
          error={fields.show('description', errorOf('description_long'))}
          name="description"
          form={fields}
          testid="ruleset-duplicate-description"
        >
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <div className="small muted">{t('rulesets.duplicate.note')}</div>
      </div>
    </Dialog>
  );
}

/** The admin service's refusal in words — its own message for a code this screen does not know. */
function errorText(e: unknown): string {
  // R8b per-rule-set permissions
  const forbidden = ruleSetForbiddenMessage(e);
  if (forbidden) return forbidden;
  if (e instanceof ApiError && e.code) {
    const key = `rulesets.error.${e.code}`;
    const known = ERROR_CODES.has(e.code);
    if (known) return t(key);
  }
  return e instanceof Error ? e.message : String(e);
}

const ERROR_CODES = new Set([
  'registry_conflict',
  'rule_set_exists',
  'rule_set_primary',
  'rule_set_enabled',
  'rule_set_archived',
  'rule_set_disabled',
  'rule_set_not_released',
  'rule_sets_need_cosmos',
  'no_primary_rule_set',
  // Wave 5: the duplicate's source is gone
  'unknown_rule_set',
]);
